/**
 * Admin → System Health
 *
 * Shows vendor balance health for Kling AI, ElevenLabs, and Emergent LLM.
 * Hits GET /api/admin/system-health on mount + every 60s while the tab is
 * focused. Also exposes a "Send digest now" button that triggers the email.
 */

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  RefreshCw,
  Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000;

const LEVEL_STYLE = {
  green:  { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-700", Icon: CheckCircle2, badge: "OK" },
  yellow: { bg: "bg-amber-500/10",   border: "border-amber-500/50",   text: "text-amber-700",   Icon: AlertTriangle, badge: "WARN" },
  red:    { bg: "bg-rose-500/10",    border: "border-rose-500/50",    text: "text-rose-700",    Icon: AlertCircle,   badge: "URGENT" },
  gray:   { bg: "bg-ink-100",        border: "border-ink-300",        text: "text-ink-700",     Icon: Activity,      badge: "—" },
};

export default function AdminSystemHealthPage() {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/system-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnapshot(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load system health");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  const handleSendDigest = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/admin/system-health/send-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Digest email dispatched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally {
      setSending(false);
    }
  };

  const handleCleanupReels = async () => {
    setCleaning(true);
    try {
      // 1) dry run first so we never delete blindly
      if (!cleanupPreview) {
        const dry = await axios.post(
          `${API}/api/admin/cleanup-dead-reels?dry_run=true`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCleanupPreview(dry.data);
        toast.info(`${dry.data.would_delete} dangling reel(s) found. Click again to delete.`);
        return;
      }
      // 2) confirmed delete
      const res = await axios.post(
        `${API}/api/admin/cleanup-dead-reels`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message || "Cleanup done");
      setCleanupPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const overall = snapshot?.overall || "ok";
  const overallStyle =
    overall === "urgent" ? "red" :
    overall === "warn"   ? "yellow" :
    overall === "error"  ? "gray" :
    overall === "unknown"? "gray" :
                           "green";
  const OverallIcon = LEVEL_STYLE[overallStyle].Icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-system-health-page"
    >
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">System health</p>
          <h1 className="text-3xl font-bold text-ink-950 mt-1">Vendor balances</h1>
          <p className="text-ink-500 mt-2 max-w-xl">
            Monitors Kling AI, ElevenLabs, and the Emergent LLM key. A digest email
            goes to the admin daily at 00:02 Cyprus time when anything is below threshold.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSnapshot}
            disabled={loading}
            data-testid="system-health-refresh-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSendDigest}
            disabled={sending}
            data-testid="system-health-send-digest-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Send digest now
          </Button>
          <Button
            variant={cleanupPreview && cleanupPreview.would_delete > 0 ? "destructive" : "outline"}
            size="sm"
            onClick={handleCleanupReels}
            disabled={cleaning}
            data-testid="cleanup-dead-reels-btn"
            title="Remove community posts whose video URL points to ephemeral disk (404s after redeploy)."
          >
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {cleanupPreview
              ? (cleanupPreview.would_delete > 0
                  ? `Confirm delete ${cleanupPreview.would_delete}`
                  : "No dead reels")
              : "Cleanup dead reels"}
          </Button>
        </div>
      </header>

      {/* Overall pill */}
      <div
        className={`rounded-2xl border ${LEVEL_STYLE[overallStyle].border} ${LEVEL_STYLE[overallStyle].bg} p-4 flex items-center gap-3`}
        data-testid={`system-health-overall-${overallStyle}`}
      >
        <OverallIcon className={`w-5 h-5 ${LEVEL_STYLE[overallStyle].text}`} />
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.18em] ${LEVEL_STYLE[overallStyle].text}`}>
            {overall === "ok" ? "All systems go" : `Status: ${overall}`}
          </p>
          {snapshot?.checked_at && (
            <p className="text-[11px] text-ink-500 mt-0.5">
              Last checked: {new Date(snapshot.checked_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Provider grid */}
      {loading && !snapshot ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-coral-500 animate-spin" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(snapshot?.providers || []).map((p) => {
            const style = LEVEL_STYLE[p.level || "gray"];
            const Icon = style.Icon;
            return (
              <motion.div
                key={p.provider}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border ${style.border} ${style.bg} p-5 flex flex-col gap-3`}
                data-testid={`provider-card-${p.provider}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${style.text}`} />
                    <h3 className="font-bold text-ink-950 text-base">{p.label}</h3>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-[0.14em] ${style.text} px-2 py-0.5 rounded-full border ${style.border}`}>
                    {style.badge}
                  </span>
                </div>

                {p.percent !== null && p.percent !== undefined && (
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                        Remaining
                      </span>
                      <span className="text-2xl font-bold text-ink-950">
                        {p.percent.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-ink-200 overflow-hidden">
                      <div
                        className={`h-full ${
                          p.level === "red" ? "bg-rose-500" :
                          p.level === "yellow" ? "bg-amber-500" :
                          "bg-emerald-500"
                        }`}
                        style={{ width: `${Math.max(2, Math.min(p.percent, 100))}%` }}
                      />
                    </div>
                    {p.remaining !== undefined && (
                      <p className="text-[11px] text-ink-500 mt-2">
                        {p.remaining.toLocaleString()} / {p.limit.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-sm text-ink-700">{p.message}</p>

                {p.action_url && (
                  <a
                    href={p.action_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-ink-950 hover:text-coral-500 mt-auto pt-1"
                    data-testid={`provider-action-${p.provider}`}
                  >
                    Top up <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
